import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactDOM from 'react-dom';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  ArrowDownTrayIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { useLanguage } from '../contexts/LanguageContext';

const DEFAULT_PAGE_SIZE = 50;
// Every paginated endpoint caps a single page at 100; we walk pages of that
// size when exporting. In-memory sources ignore the cap harmlessly.
const EXPORT_PAGE_SIZE = 100;
// Safety ceiling so a huge date range can't spawn hundreds of export requests.
const EXPORT_MAX_ROWS = 10000;

const csvEscape = (value) => {
  const s = value == null ? '' : String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadCsv = (filename, rows) => {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const lines = [
    headers.join(','),
    ...rows.map((r) => headers.map((h) => csvEscape(r[h])).join(',')),
  ];
  // Prefix BOM so Excel reads UTF-8 correctly.
  const blob = new Blob(['﻿' + lines.join('\n')], {
    type: 'text/csv;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Generic "view all" list modal — paginated, filterable, CSV-exportable.
 *
 * The modal owns all chrome (header, filter, pagination, export, loading /
 * empty / error states); the caller supplies only the data plumbing:
 *
 *   loadPage(offset, limit, q) => Promise<{ items, total }>   how to fetch a page
 *   renderRow(item, rank)      => ReactNode                   how one row looks
 *   csvRow(item, rank)         => object                      one CSV record (optional)
 *
 * Because the interface is uniform, the same component drives server-paginated
 * sources (search terms, top products) and in-memory ones (countries) — the
 * caller just slices an array inside loadPage.
 */
const StatListModal = ({
  open,
  onClose,
  title,
  icon = null,
  loadPage,
  renderRow,
  filterable = false,
  filterPlaceholder = '',
  csvRow = null,
  csvFilename = null,
  pageSize = DEFAULT_PAGE_SIZE,
}) => {
  const { t } = useLanguage();

  const [rawQuery, setRawQuery] = useState('');
  const [query, setQuery] = useState(''); // debounced, passed to loadPage
  const [page, setPage] = useState(1); // 1-based
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const formatNum = (n) => (n || 0).toLocaleString('de-CH');

  // Reset filter + page each time the modal opens.
  useEffect(() => {
    if (open) {
      setRawQuery('');
      setQuery('');
      setPage(1);
    }
  }, [open]);

  // Debounce the filter input → query.
  useEffect(() => {
    const id = setTimeout(() => {
      setQuery(rawQuery.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(id);
  }, [rawQuery]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Fetch the current page whenever inputs change.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setIsLoading(true);
    setError(false);
    Promise.resolve(loadPage((page - 1) * pageSize, pageSize, query))
      .then((res) => {
        if (cancelled) return;
        setItems(Array.isArray(res?.items) ? res.items : []);
        setTotal(res?.total || 0);
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
          setItems([]);
          setTotal(0);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, page, query, pageSize, loadPage]);

  const rangeLabel = useMemo(() => {
    if (!total) return '';
    const first = (page - 1) * pageSize + 1;
    const last = Math.min(page * pageSize, total);
    return t('statistics.listModal.showing', {
      from: formatNum(first),
      to: formatNum(last),
      total: formatNum(total),
    });
  }, [page, total, pageSize, t]);

  const handleExportAll = async () => {
    if (isExporting || !csvRow || !csvFilename) return;
    setIsExporting(true);
    try {
      const rows = [];
      let offset = 0;
      // Page through the full (filtered) set up to the safety ceiling.
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const res = await Promise.resolve(
          loadPage(offset, EXPORT_PAGE_SIZE, query)
        );
        const batch = Array.isArray(res?.items) ? res.items : [];
        const grandTotal = res?.total || 0;
        const base = offset; // capture before mutating for the closure below
        batch.forEach((item, i) => rows.push(csvRow(item, base + i + 1)));
        offset += batch.length;
        if (
          batch.length < EXPORT_PAGE_SIZE ||
          offset >= grandTotal ||
          offset >= EXPORT_MAX_ROWS
        ) {
          break;
        }
      }
      downloadCsv(csvFilename(query), rows);
    } catch (err) {
      setError(true);
    } finally {
      setIsExporting(false);
    }
  };

  if (!open) return null;

  return ReactDOM.createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.98, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.98, y: 10 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="relative flex flex-col overflow-hidden bg-white shadow-2xl border border-gray-200 rounded-2xl w-full max-w-2xl max-h-[90vh]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              {icon}
              {title}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label={t('statistics.listModal.close')}
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Toolbar: filter + export */}
          {(filterable || (csvRow && csvFilename)) && (
            <div className="flex items-center gap-3 px-6 py-3 border-b border-gray-100">
              {filterable && (
                <div className="relative flex-1">
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    autoFocus
                    value={rawQuery}
                    onChange={(e) => setRawQuery(e.target.value)}
                    placeholder={filterPlaceholder}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              )}
              {!filterable && <div className="flex-1" />}
              {csvRow && csvFilename && (
                <button
                  onClick={handleExportAll}
                  disabled={isExporting || total === 0}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  {isExporting
                    ? t('statistics.listModal.exporting')
                    : t('statistics.listModal.exportAll')}
                </button>
              )}
            </div>
          )}

          {/* List */}
          <div className="flex-1 overflow-y-auto px-6 py-3 min-h-[240px]">
            {isLoading ? (
              <div className="text-center py-16">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-indigo-500 border-r-transparent" />
              </div>
            ) : error ? (
              <p className="text-sm text-red-500 py-16 text-center">
                {t('statistics.listModal.error')}
              </p>
            ) : items.length === 0 ? (
              <p className="text-sm text-gray-400 py-16 text-center">
                {t('statistics.listModal.empty')}
              </p>
            ) : (
              <div className="divide-y divide-gray-100">
                {items.map((item, i) => {
                  const rank = (page - 1) * pageSize + i + 1;
                  return (
                    <div key={rank} className="py-2.5">
                      {renderRow(item, rank)}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer: pagination */}
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 text-sm text-gray-500">
            <span>{rangeLabel}</span>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1 || isLoading}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">
                    {t('statistics.listModal.prev')}
                  </span>
                </button>
                <span className="text-gray-600 tabular-nums">
                  {t('statistics.listModal.page', {
                    page: formatNum(page),
                    pages: formatNum(totalPages),
                  })}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages || isLoading}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="hidden sm:inline">
                    {t('statistics.listModal.next')}
                  </span>
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default StatListModal;
